import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import {
  broadcastEventMessageSchema,
  broadcastEventMessageType,
  contentChangedHeightMessageSchema,
  contentChangedHeightMessageType,
  Event,
  hostAppReadyMessageType,
  LoadMicroFrontendMessage,
  loadMicroFrontendMessageType,
  messageDataSchema,
  PublishEventMessage,
  publishEventMessageType,
} from './micro-frontend-schemas/messages';

// The paths to the HTML documents that load the React apps for micro frontends. Their source code
// is located at `rind/micro-frontend-iframe` and `rind/federated-component-iframe`. When they are
// built, their output is copied to the `target/bktools/micro-frontend-iframe` and
// `target/bktools/federated-component-iframe` directories so they can be served on the same origin
// as the rind apps, for security reasons.
//
// This feature was originally implemented using @benchlabs/react-lazy-micro-frontend, which is what
// micro-frontend-iframe uses. From now on, all micro frontends should use
// federated-component-iframe instead, since it uses @benchlabs/react-federated-component and
// takes advantage of the micro frontend registry.
const microFrontendIframeSrc = '/app/micro-frontend-iframe/index.html';
const federatedComponentIframeSrc = '/app/federated-component-iframe/index.html';

// Which iframe src value is used depends on what library was used to create the micro frontend
// being mounted - the deprecated @benchlabs/react-lazy-micro-frontend mode ('lazy') or the
// @benchlabs/react-federated-component mode ('federated').
export type Mode = 'lazy' | 'federated';

const modeToSrc = {
  lazy: microFrontendIframeSrc,
  federated: federatedComponentIframeSrc,
};

// There are two presentational contracts for micro frontends in iframes:
// - 'content': the iframe's height is based on the height of its content (which requires messages
//   containing the height of the content to be sent from the iframe)
// - 'fill': the iframe fills the space it's given using `height: 100%`.
type HeightVariant = 'content' | 'fill';

const cssInitialHeightValues: Record<HeightVariant, string> = {
  content: '0px',
  fill: '100%',
};

// Iframes each need unique title properties so that users of assistive technology can differentiate
// each one. A unique ID for each iframe is generated by incrementing this value by one each time
// an iframe gets mounted.
//
// This is not a very good solution because it doesn't provide any useful information about what's
// in the iframe. A better solution would be for the `MicroFrontend` component to accept a `title`
// prop, however, since this is a bridge solution, and ultimately the micro frontends will not be
// loaded in iframes, it satisfies the minimum requirement to have unique titles.
let nextIframeRefId = 0;

// Since there's one global `window.postMessage()` event handler, we need to keep track of all the
// iframes that are mounted by this component and then use the iframe's `contentWindow` to identify
// which iframe sent a message.
//
// Props that are required to respond to certain message types are stored in the map.
const iframeRefs: Map<
  RefObject<HTMLIFrameElement>,
  {
    name: string;
    data?: Record<string, unknown>;
    env?: Record<string, unknown>;
    height: HeightVariant;
  }
> = new Map();

const handleFrameMounted = (
  iframeRef: RefObject<HTMLIFrameElement>,
  name: string,
  data: Record<string, unknown> | undefined,
  env: Record<string, unknown> | undefined,
  height: HeightVariant,
) => {
  iframeRefs.set(iframeRef, { name, data, env, height });
  const iframeRefId = nextIframeRefId;
  nextIframeRefId += 1;
  return iframeRefId;
};

const handleFrameUnmounted = (iframeRef: RefObject<HTMLIFrameElement>) => {
  iframeRefs.delete(iframeRef);
};

// rind itself can subscribe to events published by micro frontends. This is used when
// capabilities provided by the host app have event-based interfaces, such as the layout's ability
// to open a "secondary content" area.
type HandlerFn = (detail?: Record<string, unknown>) => void;

interface Subscription {
  eventType: string;
  handler: HandlerFn;
}

const subscriptions: Subscription[] = [];

/**
 * Subscribe to events published by micro frontends. The handler function will be called with the
 * detail of the event.
 *
 * @returns an unsubscribe function.
 */
export const subscribe = (eventType: string, handler: HandlerFn) => {
  subscriptions.push({ eventType, handler });
  return () => {
    const index = subscriptions.findIndex(subscription => subscription.handler === handler);
    if (index !== -1) {
      subscriptions.splice(index, 1);
    }
  };
};

// This broadcasts an event to all mounted iframes (except the one that published the event) and
// to the subscribers in rind.
const broadcastEvent = (sourceIframeRef: RefObject<HTMLIFrameElement>, event: Event) => {
  for (const [iframeRef] of iframeRefs) {
    if (!iframeRef.current) continue;
    if (!iframeRef.current.contentWindow) continue;

    // Don't publish the event back to its originating app, otherwise the event will be duplicated
    // on that event bus.
    if (iframeRef === sourceIframeRef) continue;

    const publishEventMessage: PublishEventMessage = {
      type: publishEventMessageType,
      detail: { event },
    };
    iframeRef.current.contentWindow.postMessage(publishEventMessage, window.location.origin);
  }
  for (const subscription of subscriptions) {
    if (subscription.eventType === event.type) {
      subscription.handler(event.detail);
    }
  }
};

// Resize the iframe in response to a message from the iframe reporting the current height of its
// content.
const resizeIframe = (iframeRef: RefObject<HTMLIFrameElement>, height: number) => {
  if (!iframeRef.current) return;
  if (!iframeRefs.has(iframeRef)) return;

  // Only resize the iframe if its `height` prop is set to 'content'.
  if (iframeRefs.get(iframeRef)?.height !== 'content') return;

  iframeRef.current.style.height = `${height}px`;
};

// Send a message to the iframe window telling it to load the specific micro frontend into its
// React app.
const loadMicroFrontend = (
  iframe: HTMLIFrameElement,
  name: string,
  data?: Record<string, unknown>,
  env?: Record<string, unknown>,
) => {
  if (!iframe.contentWindow) return;

  const loadMicroFrontendMessage: LoadMicroFrontendMessage = {
    type: loadMicroFrontendMessageType,
    detail: { props: { name, data, env } },
  };
  iframe.contentWindow.postMessage(loadMicroFrontendMessage, window.location.origin);
};

// Routes messages from iframes to handler functions.
const handleWindowMessage = (event: MessageEvent) => {
  // Only accept messages from the same origin as the host app. This prevents us from processing
  // messages from potentially malicious sources. We trust the code served on our own domains.
  if (event.origin !== window.location.origin) return;

  // Find the iframe that sent the message in the index based on the iframe's contentWindow.
  const entry = Array.from(iframeRefs.entries()).find(
    ([ref]) => ref.current?.contentWindow === event.source,
  );

  // Don't process messages that aren't sent from iframes that this component mounted. They're
  // probably from a different feature or a third-party library.
  if (!entry) return;

  const [iframeRef, { name, data, env }] = entry;

  // A message shouldn't arrive if the iframe isn't mounted. This is for type safety.
  if (!iframeRef || !iframeRef.current) return;

  const parseOutput = messageDataSchema.safeParse(event.data);

  // Don't process messages that don't conform to the expected schema. This helps prevents us from
  // processing messages that aren't meant for this app.
  if (!parseOutput.success) return;

  const messageData = parseOutput.data;

  switch (messageData.type) {
    case hostAppReadyMessageType:
      loadMicroFrontend(iframeRef.current, name, data, env);
      break;
    case contentChangedHeightMessageType:
      const contentChangedHeightMessage = contentChangedHeightMessageSchema.parse(messageData);
      resizeIframe(iframeRef, contentChangedHeightMessage.detail.height);
      break;
    case broadcastEventMessageType:
      const broadcastEventMessage = broadcastEventMessageSchema.parse(messageData);
      broadcastEvent(iframeRef, broadcastEventMessage.detail.event);
      break;
  }
};

// There is one global event listener for `window.postMessage()` events. The iframeRefs are used to
// determine which iframe sent the message. This is done instead of adding an event listener for
// each iframe.
window.addEventListener('message', handleWindowMessage);

export interface MicroFrontendProps {
  // Name of the micro frontend in the component registry.
  name: string;

  // Data that the micro frontend requires in order to present its content.
  data?: Record<string, unknown>;

  // How the height of the iframe should behave. Either updates based on the height of its content
  // ('content') or fill the space where it's mounted using `height: 100%` ('fill'). Defaults to
  // 'content' since that's how normal HTML content behaves.
  height?: HeightVariant;

  // Indicates which library was used to create the micro frontend being mounted.
  mode?: Mode;
}

/**
 * Mimics the API of the real MicroFrontend component in @benchlabs/react-lazy-micro-frontend with
 * some props omitted.
 *
 * In order to allow micro frontends to use modern React features without worrying about
 * compatibility with the legacy host app, this component loads the micro frontend in a modern host
 * app in an iframe.
 *
 * If multiple iframes are mounted at the same time, their event buses are bridged so that events
 * published in one iframe are broadcast to the others.
 *
 * Of the issues that come along with iframes, the most visible one from this component is that the
 * iframe's height is not automatically adjusted to fit its content. This is worked around by
 * sending a message from the iframe when its content changes that tells this component what the
 * height of the content is.
 *
 * Not all micro frontends need to have their height adjusted. There are two presentational
 * contracts that need to be handled: micro frontends whose height is based on their content, and
 * micro frontends that fill the space they're given. The latter type will have the iframe set to
 * 100% height and width, and will ignore the resize messages.
 *
 * The default height variant is 'content' because it behaves like normal HTML content.
 */
export const MicroFrontend: FC<MicroFrontendProps> = ({
  name,
  data,
  height = 'content',
  mode = 'federated',
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const env = window.BENCH.mfe;

  // This is only used to create unique titles for each iframe.
  const [iframeRefId, setIframeRefId] = useState(0);

  // Keep an index of the iframe refs and their props that can be used to identify the iframe in the
  // global message event handler.
  useEffect(() => {
    const newIframeRefId = handleFrameMounted(iframeRef, name, data, env, height);
    setIframeRefId(newIframeRefId);
    return () => handleFrameUnmounted(iframeRef);
  }, [iframeRef, name, data, height, env]);

  // If `name` or `data` change, reload the micro frontend to reflect the new props.
  useEffect(() => {
    if (!iframeRef.current) return;
    loadMicroFrontend(iframeRef.current, name, data, env);
  }, [iframeRef, name, data, env]);

  return (
    <iframe
      ref={iframeRef}
      title={`Embedded micro application ${iframeRefId}`}
      src={modeToSrc[mode]}
      style={{
        border: 'none',
        height: cssInitialHeightValues[height],
        width: '100%',
      }}
    />
  );
};
